import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { NotificationBell } from "@/components/NotificationBell";
import { LiveClock } from "@/components/LiveClock";
import { OnlineStaffBadge } from "@/components/OnlineStaffBadge";
import { EmailHealthBadge } from "@/components/EmailHealthBadge";
import { PartnerOfferPopup } from "@/components/PartnerOfferPopup";
import { api } from "@/lib/api";
import MobileAppDownloadButton from "@/components/MobileAppDownloadButton";
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  FileText,
  UserCog,
  LogOut,
  Building2,
  Smartphone,
  Briefcase,
  Coins,
  FolderTree,
  Layers,
  Clock,
  MessageSquare,
  CreditCard,
  Wallet,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Settings,
  Zap,
  X,
  KeyRound,
  Euro,
  BookUser,
  ListPlus,
  Lightbulb,
  FileSearch,
  Volume2,
  VolumeX,
  BellRing,
  Bell,
  BellOff,
  Inbox as InboxIcon,
  StickyNote,
  Calendar,
  Mail,
  FolderLock,
  Send as SendIcon,
  Package,
  MessageSquareText,
  Repeat,
  PenTool,
  CalendarClock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { QuickNoteFab } from "@/components/QuickNoteFab";
import { TextZoomControl } from "@/components/TextZoomControl";
import DownloadProgressOverlay from "@/components/DownloadProgressOverlay";

const ROLE_LABEL = { admin: "Amministratore", agente: "Agente", backoffice: "Back-office", collaboratore: "Collaboratore" };
const ROLE_BADGE = {
  admin: "bg-amber-400/15 text-amber-200 border-amber-300/30",
  agente: "bg-emerald-400/15 text-emerald-200 border-emerald-300/30",
  backoffice: "bg-blue-400/15 text-blue-200 border-blue-300/30",
  collaboratore: "bg-slate-400/15 text-slate-200 border-slate-300/30",
};

// Sidebar structure: flat items + collapsible groups.
const NAV_TOP = [
  { to: "/superadmin", label: "Gestione Agenzie SaaS", icon: Building2, roles: ["super_admin"] },
  { to: "/dashboard", label: "Cruscotto", icon: LayoutDashboard, roles: ["admin", "agente", "backoffice", "collaboratore"] },
  { to: "/clients", label: "Clienti", icon: Users, roles: ["admin", "agente", "backoffice", "collaboratore"], permission: "clients" },
  { to: "/leads", label: "↳ Lead in attesa", icon: Users, roles: ["admin", "agente", "backoffice", "collaboratore"], permission: "clients" },
  { to: "/policies", label: "Polizze", icon: ShieldCheck, roles: ["admin", "agente", "backoffice", "collaboratore"], permission: "policies", highlight: "yellow" },
  { to: "/policies/deadlines", label: "↳ Scadenziario", icon: CalendarClock, roles: ["admin", "agente", "backoffice", "collaboratore"], permission: "policies" },
  { to: "/quotes", label: "Preventivi", icon: FileText, roles: ["admin", "agente", "backoffice", "collaboratore"], permission: "quotes", highlight: "green", badge: "quotes_total" },
  { to: "/quotes/offers", label: "↳ Offerte finali", icon: Euro, roles: ["admin", "agente", "backoffice", "collaboratore"], permission: "quotes" },
  { to: "/signatures", label: "✍️ Firme OTP", icon: FileText, roles: ["admin", "agente", "backoffice"] },
  { to: "/signature-center", label: "📝 Centro Firme", icon: PenTool, roles: ["admin", "agente", "backoffice"] },
  { to: "/inbox", label: "Client Email", icon: InboxIcon, roles: ["admin", "agente", "backoffice"], badge: "inbox" },
  { to: "/notes", label: "📌 Appunti rapidi", icon: StickyNote, roles: ["admin", "agente", "backoffice", "collaboratore"], highlight: "green" },
  { to: "/agenda", label: "📅 Agenda", icon: Calendar, roles: ["admin", "agente", "backoffice", "collaboratore"] },
  { to: "/rubrica", label: "📞 Rubrica", icon: BookUser, roles: ["admin", "agente", "backoffice"] },
  { to: "/agency/closures", label: "📣 Chiusure", icon: Calendar, roles: ["admin"] },
  { to: "/messaggi", label: "Messaggi interni", icon: MessageSquare, roles: ["admin", "agente", "backoffice", "collaboratore"], badge: "messages" },
  { to: "/commissions", label: "Provvigioni", icon: Coins, roles: ["admin", "agente", "backoffice"], permission: "commissions", highlight: "pink" },
  { to: "/commissions/collected", label: "💰 Provvigioni incassate", icon: Coins, roles: ["admin", "agente", "backoffice"], permission: "commissions" },
  { to: "/cash-register", label: "💵 Registro Incassi", icon: Wallet, roles: ["admin", "backoffice"], permission: "payments" },
  { to: "/commissions/broker-report", label: "🏆 Report Broker", icon: Coins, roles: ["admin", "backoffice"], permission: "commissions" },
  { to: "/admin/pagamenti", label: "Pagamenti", icon: Euro, roles: ["admin", "backoffice"], badge: "payments" },
];

const NAV_GROUPS = [
  {
    key: "polizze",
    label: "Gest. sez. Polizze",
    icon: ShieldCheck,
    items: [
      { to: "/admin/categories", label: "Categorie Polizze", icon: FolderTree, roles: ["admin", "backoffice"], permission: "categories" },
      { to: "/admin/policy-types", label: "Tipologie Polizze", icon: Layers, roles: ["admin", "backoffice"], permission: "categories" },
      { to: "/admin/policy-coverages", label: "Garanzie Polizze", icon: ShieldCheck, roles: ["admin", "backoffice"], permission: "categories" },
      { to: "/brokers", label: "Broker/Agenzie", icon: Briefcase, roles: ["admin", "backoffice"], permission: "brokers" },
      { to: "/companies", label: "Compagnie", icon: Building2, roles: ["admin", "backoffice"], permission: "companies" },
    ],
  },
  {
    key: "agenzia",
    label: "Gestione Agenzia",
    icon: Settings,
    items: [
      { to: "/admin/agency", label: "Anagrafica Agenzia", icon: Building2, roles: ["admin"] },
      { to: "/agency/attachments", label: "📎 Allegati Agenzia", icon: FolderLock, roles: ["admin", "agente", "backoffice"] },
      { to: "/agency/email-settings", label: "📧 Impostazioni Email", icon: Mail, roles: ["admin"] },
      { to: "/agency/public-page", label: "🌐 Area pubblica preventivi", icon: Mail, roles: ["admin"] },
      { to: "/agency/products", label: "Catalogo Prodotti", icon: Package, roles: ["admin", "backoffice", "agente"] },
      { to: "/agency/receipts", label: "🧾 Ricevute", icon: FileSearch, roles: ["admin", "backoffice"] },
      { to: "/admin/stripe-test", label: "🔬 Test Stripe", icon: Euro, roles: ["admin"] },
      {
        // 🆕 Feb 2026 — Sotto-gruppo "Impost. Gestionale" (verde)
        subgroup: true,
        key: "impost-gestionale",
        label: "Impost. Gestionale",
        icon: Settings,
        tone: "emerald",
        roles: ["admin", "agente", "backoffice"],
        items: [
          { to: "/agency/tax-rates", label: "% Aliquote Imposte/SSN", icon: Mail, roles: ["admin", "backoffice"] },
          { to: "/agency/replacement-reasons", label: "🔄 Causali sostituzione", icon: Repeat, roles: ["admin", "backoffice"] },
          { to: "/admin/pagamenti/config", label: "💳 Config. Pagamenti", icon: Euro, roles: ["admin"], requireOtp: true },
          { to: "/admin/automations", label: "Gestione Automazioni", icon: Zap, roles: ["admin", "backoffice"] },
          { to: "/admin/offer-line-items", label: "Voci offerta", icon: ListPlus, roles: ["admin", "backoffice"] },
          { to: "/admin/lookup-lists", label: "📚 Liste preventivo", icon: ListPlus, roles: ["admin"] },
          { to: "/admin/message-templates", label: "💬 Gestione Modelli", icon: MessageSquareText, roles: ["admin"] },
          { to: "/admin/pdf-templates", label: "📄 Modelli PDF – Policy/Quote", icon: FileText, roles: ["admin", "backoffice"] },
          { to: "/admin/required-documents", label: "Documenti richiesti", icon: FileSearch, roles: ["admin", "backoffice"] },
          { to: "/admin/quote-notifications", label: "Notifiche preventivi", icon: BellRing, roles: ["admin"] },
          { to: "/admin/insights", label: "Insight commerciali", icon: Lightbulb, roles: ["admin", "backoffice", "agente"] },
          { to: "/profile/notifications", label: "Le mie notifiche", icon: Bell, roles: ["admin", "agente", "backoffice"] },
        ],
      },
      { to: "/users", label: "Utenti", icon: UserCog, roles: ["admin"] },
      { to: "/admin/cron", label: "Cron Job", icon: Clock, roles: ["admin"] },
      { to: "/admin/cf-cleanup", label: "🧹 Bonifica CF", icon: FileSearch, roles: ["admin", "backoffice"] },
      { to: "/portal", label: "Portale Cliente", icon: Smartphone, roles: ["admin", "agente", "backoffice"] },
      { action: "change-password", label: "Cambia password", icon: KeyRound, roles: ["admin", "agente", "backoffice"] },
      { to: "/profile/security", label: "🔐 Sicurezza (OTP)", icon: KeyRound, roles: ["admin", "agente", "backoffice", "super_admin"] },
    ],
  },
];

const hasNavAccess = (user, item) => {
  if (!item.roles.includes(user?.role)) return false;
  if (!item.permission) return true;
  if (user?.role === "admin") return true;
  const perms = user?.permissions || {};
  return perms[item.permission] && perms[item.permission] !== "none";
};

export const Layout = () => {
  const { user, logout } = useAuth();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const location = useLocation();
  const visibleTop = useMemo(() => NAV_TOP.filter((n) => hasNavAccess(user, n)), [user]);
  const visibleGroups = useMemo(() => NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items
        .map((it) => {
          // 🆕 Feb 2026 — filtra anche i sub-group annidati
          if (it.subgroup) {
            const subItems = (it.items || []).filter((sub) => hasNavAccess(user, sub));
            return { ...it, items: subItems };
          }
          return it;
        })
        .filter((it) => {
          if (it.subgroup) {
            if (it.roles && !it.roles.includes(user?.role)) return false;
            return (it.items || []).length > 0;
          }
          return hasNavAccess(user, it);
        }),
    }))
    .filter((g) => g.items.length > 0), [user]);

  // Default-open groups whose currently active route belongs to them
  const initialOpen = useMemo(() => {
    const open = {};
    for (const g of visibleGroups) {
      // Un item o un sub-item corrispondente alla route attiva → gruppo aperto
      const inGroup = g.items.some((it) => {
        if (it.subgroup) return (it.items || []).some((s) => s.to && location.pathname.startsWith(s.to));
        return it.to && location.pathname.startsWith(it.to);
      });
      open[g.key] = inGroup;
      // 🆕 Feb 2026 — auto-apri anche i sub-group se contengono la route attiva
      for (const it of g.items) {
        if (it.subgroup) {
          open[`sub-${it.key}`] = (it.items || []).some((s) => s.to && location.pathname.startsWith(s.to));
        }
      }
    }
    return open;
  }, [visibleGroups, location.pathname]);
  const [openGroups, setOpenGroups] = useState(initialOpen);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingPayments, setPendingPayments] = useState(0);
  const [quotesTotal, setQuotesTotal] = useState(0);
  const [pendingReplies, setPendingReplies] = useState(0);
  const [hotNewLeads, setHotNewLeads] = useState(0);
  // Suono notifica nuovi lead (toggle persistito in localStorage)
  const [audioEnabled, setAudioEnabled] = useState(() => {
    try { return localStorage.getItem("crm_lead_audio") !== "off"; } catch { return true; }
  });
  // Info agenzia per banner scadenza
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [payingPending, setPayingPending] = useState(false);
  // 🆕 Feb 2026 — Popup demo agency (ad ogni apertura app se agenzia.is_demo)
  const [demoPopupOpen, setDemoPopupOpen] = useState(false);
  useEffect(() => {
    if (agencyInfo?.agency?.is_demo === true) {
      setDemoPopupOpen(true);
    }
  }, [agencyInfo?.agency?.is_demo]);
  // Ref per tenere traccia del precedente valore senza ritrigger
  const prevHotLeadsRef = useRef(null);
  const prevUnreadMsgRef = useRef(null);
  const [appVersion, setAppVersion] = useState(null);
  // Cambio password staff
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  // 🆕 Sidebar collapse: 3-state persistente (null = auto | "collapsed" | "expanded")
  const [sidebarMode, setSidebarMode] = useState(() => {
    try {
      const v = localStorage.getItem("sidebar_mode");
      return v === "collapsed" || v === "expanded" ? v : null;
    } catch { return null; }
  });
  const [autoCollapsed, setAutoCollapsed] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1280 : false
  );
  useEffect(() => {
    const check = () => setAutoCollapsed(window.innerWidth < 1280);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  // Priorità: preferenza manuale > auto
  const isCollapsed = sidebarMode === "collapsed" ? true : sidebarMode === "expanded" ? false : autoCollapsed;
  const toggleManualCollapse = () => {
    const next = isCollapsed ? "expanded" : "collapsed";
    setSidebarMode(next);
    try { localStorage.setItem("sidebar_mode", next); } catch { /* ignore */ }
  };
  // legacy: se la preferenza precedente era "sidebar_collapsed_manual", migra a nuovo formato
  useEffect(() => {
    try {
      const legacy = localStorage.getItem("sidebar_collapsed_manual");
      if (legacy != null && !localStorage.getItem("sidebar_mode")) {
        const migrated = legacy === "1" ? "collapsed" : "expanded";
        localStorage.setItem("sidebar_mode", migrated);
        setSidebarMode(migrated);
        localStorage.removeItem("sidebar_collapsed_manual");
      }
    } catch { /* ignore */ }
  }, []);

  const submitChangePassword = async () => {
    if (!pwdForm.current || !pwdForm.next) {
      toast.error("Compila tutti i campi");
      return;
    }
    if (pwdForm.next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdForm.current, new_password: pwdForm.next });
      toast.success("Password aggiornata con successo");
      setPwdOpen(false);
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwdBusy(false);
    }
  };

  useEffect(() => {
    // Fetch versione una volta sola al mount (e ad ogni cambio utente login)
    let cancelled = false;
    api.get("/version").then(({ data }) => {
      if (!cancelled) setAppVersion(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // Fetch info agenzia per banner scadenza
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/agency/me");
        if (!cancelled) setAgencyInfo(data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const notifyPayment = async () => {
    const note = window.prompt("Inserisci una nota (es. data bonifico, riferimento PayPal):", "");
    if (note === null) return;
    setPayingPending(true);
    try {
      await api.post("/agency/payment-notification", { note });
      toast.success("Notifica inviata al super-admin. Verrà riattivata a breve.");
      const { data } = await api.get("/agency/me");
      setAgencyInfo(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setPayingPending(false); }
  };

  const [unreadInbox, setUnreadInbox] = useState(0);
  const [freshInboxAt, setFreshInboxAt] = useState(null); // timestamp ms quando arrivano nuove email
  const [notesCount, setNotesCount] = useState(0);
  const inboxRefreshRef = useRef(null);
  const notesRefreshRef = useRef(null);
  const lastInboxCountRef = useRef(null);

  // Refresh badge Inbox al cambio di rotta (es: ritorno da /inbox dopo aver elaborato email)
  useEffect(() => {
    if (location.pathname === "/inbox") {
      setFreshInboxAt(null); // azzera l'animazione rossa quando l'utente entra in /inbox
    }
    if (inboxRefreshRef.current) inboxRefreshRef.current();
    if (notesRefreshRef.current) notesRefreshRef.current();
  }, [location.pathname]);

  // Tick ogni 5s per far decadere l'animazione "fresh" dopo 60s
  useEffect(() => {
    if (!freshInboxAt) return;
    const t = setInterval(() => {
      if (Date.now() - freshInboxAt > 60000) setFreshInboxAt(null);
    }, 5000);
    return () => clearInterval(t);
  }, [freshInboxAt]);

  useEffect(() => {
    if (!user || !["admin", "agente", "backoffice", "collaboratore"].includes(user.role)) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/chat/unread-count");
        if (!cancelled) setUnreadMessages(data.total || 0);
      } catch { /* ignore network blips */ }
    };
    const fetchPayments = async () => {
      try {
        const { data } = await api.get("/payments/notifications");
        if (!cancelled) setPendingPayments(data.count || 0);
      } catch { /* ignore network blips */ }
    };
    const fetchQuotes = async () => {
      try {
        const { data } = await api.get("/quotes/stats");
        if (!cancelled) {
          setQuotesTotal(data.total || 0);
          setPendingReplies(data.pending_replies || 0);
          setHotNewLeads(data.hot_new_leads || 0);
        }
      } catch { /* ignore network blips */ }
    };
    const fetchInbox = async () => {
      if (!["admin", "agente", "backoffice"].includes(user.role)) return;
      try {
        const { data } = await api.get("/inbox?status_filter=pending");
        if (cancelled) return;
        const count = (data || []).length;
        // Se il conteggio aumenta vs precedente → marca come "fresco" per highlight rosso
        if (lastInboxCountRef.current !== null && count > lastInboxCountRef.current && location.pathname !== "/inbox") {
          setFreshInboxAt(Date.now());
        }
        lastInboxCountRef.current = count;
        setUnreadInbox(count);
      } catch { /* ignore network blips */ }
    };
    const fetchNotes = async () => {
      try {
        const { data } = await api.get("/notes?status=active");
        if (!cancelled) setNotesCount((data || []).length);
      } catch { /* ignore network blips */ }
    };
    inboxRefreshRef.current = fetchInbox;
    notesRefreshRef.current = fetchNotes;
    fetchUnread();
    fetchPayments();
    fetchQuotes();
    fetchInbox();
    fetchNotes();
    const t1 = setInterval(fetchUnread, 10000);
    const t2 = setInterval(fetchPayments, 15000);
    const t3 = setInterval(fetchQuotes, 20000);
    const t4 = setInterval(fetchInbox, 30000);
    const t5 = setInterval(fetchNotes, 30000);
    return () => { cancelled = true; clearInterval(t1); clearInterval(t2); clearInterval(t3); clearInterval(t4); clearInterval(t5); };
  }, [user]);

  // Snapshot dei contatori per "azzeramento" (per-utente/browser): mostra solo NUOVI items dal reset
  const SNAP_KEY = `badge_snapshot_${user?.id || "anon"}`;
  const [badgeSnapshot, setBadgeSnapshot] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SNAP_KEY) || "{}"); } catch { return {}; }
  });

  const badgeFor = (key) => {
    const raw = (() => {
      if (key === "messages") return unreadMessages;
      if (key === "payments") return pendingPayments;
      if (key === "quotes_total") return quotesTotal;
      if (key === "replies") return pendingReplies;
      if (key === "inbox") return unreadInbox;
      if (key === "hot_leads") return hotNewLeads;
      return 0;
    })();
    const snap = badgeSnapshot[key] || 0;
    const delta = raw - snap;
    return delta > 0 ? delta : 0;
  };

  // Auto-clamp dello snapshot al "minimo storico" del raw count.
  // Quando l'utente lavora preventivi (total scende sotto snap), lo snapshot
  // scende a sua volta così che NUOVI arrivi facciano subito alzare il badge.
  // Senza questo, il badge resta a 0 fino a tornare sopra il vecchio snap.
  useEffect(() => {
    const updates = {};
    const map = {
      messages: unreadMessages,
      payments: pendingPayments,
      quotes_total: quotesTotal,
      replies: pendingReplies,
      inbox: unreadInbox,
      hot_leads: hotNewLeads,
    };
    Object.entries(map).forEach(([key, raw]) => {
      const snap = badgeSnapshot[key] || 0;
      if (raw != null && raw < snap) updates[key] = raw;
    });
    if (Object.keys(updates).length === 0) return;
    setBadgeSnapshot((cur) => {
      const next = { ...cur, ...updates };
      try { localStorage.setItem(SNAP_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [unreadMessages, pendingPayments, quotesTotal, pendingReplies, unreadInbox, hotNewLeads]);

  const resetAllBadges = async () => {
    const snap = {
      messages: unreadMessages || 0,
      payments: pendingPayments || 0,
      quotes_total: quotesTotal || 0,
      replies: pendingReplies || 0,
      inbox: unreadInbox || 0,
      hot_leads: hotNewLeads || 0,
      at: new Date().toISOString(),
    };
    try {
      localStorage.setItem(SNAP_KEY, JSON.stringify(snap));
      // NotificationBell ha storage separato: fetch corrente e salva snapshot
      try {
        const { data } = await api.get("/notifications");
        localStorage.setItem("notif_bell_snapshot", JSON.stringify({ count: data?.count || 0, at: snap.at }));
      } catch { /* fallback: azzera comunque */
        localStorage.setItem("notif_bell_snapshot", JSON.stringify({ count: 9999, at: snap.at }));
      }
      window.dispatchEvent(new Event("badges:reset"));
    } catch { /* noop */ }
    setBadgeSnapshot(snap);
  };

  const totalBadgeCount = (badgeFor("messages") + badgeFor("payments") + badgeFor("quotes_total") + badgeFor("replies") + badgeFor("inbox") + badgeFor("hot_leads"));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Suono "ding" discreto via WebAudio (2 toni leggeri G5→C6, ~250ms totali)
  const playDing = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const play = (freq, start, dur = 0.18) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(freq, now + start);
        g.gain.setValueAtTime(0, now + start);
        g.gain.linearRampToValueAtTime(0.18, now + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        o.connect(g).connect(ctx.destination);
        o.start(now + start);
        o.stop(now + start + dur + 0.02);
      };
      play(784, 0);     // G5
      play(1047, 0.12); // C6
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 600);
    } catch { /* audio non disponibile o blocked autoplay */ }
  };

  // Suono "click" morbido per risposte partner (singolo tono basso A4, ~140ms, onda triangle)
  const playPartnerClick = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(440, now); // A4
      o.frequency.exponentialRampToValueAtTime(330, now + 0.12);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.14, now + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
      o.connect(g).connect(ctx.destination);
      o.start(now);
      o.stop(now + 0.16);
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 400);
    } catch { /* ignore */ }
  };

  // Quando hot_new_leads aumenta, suona il ding (se abilitato)
  useEffect(() => {
    if (prevHotLeadsRef.current === null) {
      prevHotLeadsRef.current = hotNewLeads;
      return;
    }
    if (hotNewLeads > prevHotLeadsRef.current && audioEnabled) {
      playDing();
    }
    prevHotLeadsRef.current = hotNewLeads;
  }, [hotNewLeads, audioEnabled]);

  // Quando arrivano nuovi messaggi (risposte partner / chat interna), suona click morbido
  useEffect(() => {
    if (prevUnreadMsgRef.current === null) {
      prevUnreadMsgRef.current = unreadMessages;
      return;
    }
    if (unreadMessages > prevUnreadMsgRef.current && audioEnabled) {
      playPartnerClick();
    }
    prevUnreadMsgRef.current = unreadMessages;
  }, [unreadMessages, audioEnabled]);

  const toggleAudio = () => {
    setAudioEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem("crm_lead_audio", next ? "on" : "off"); } catch { /* ignore */ }
      if (next) playDing(); // anteprima ding lead
      toast.success(next ? "Suono notifiche attivato" : "Suono notifiche silenziato");
      return next;
    });
  };

  const renderNavLink = (item, indented = false) => {
    const Icon = item.icon;
    const badgeCount = item.badge ? badgeFor(item.badge) : 0;
    const highlightCls =
      item.highlight === "yellow"
        ? "text-yellow-300 hover:text-yellow-200 font-semibold"
        : item.highlight === "green"
        ? "text-emerald-300 hover:text-emerald-200 font-semibold"
        : item.highlight === "pink"
        ? "text-pink-300 hover:text-pink-200 font-semibold"
        : "text-slate-300 hover:text-white";
    // Action item (no route, opens a callback like change-password dialog)
    if (item.action === "change-password") {
      return (
        <button
          key={item.action}
          type="button"
          onClick={() => setPwdOpen(true)}
          data-testid="nav-change-password"
          title={item.label}
          className={`w-full text-left group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200 ${isCollapsed ? "justify-center" : "justify-start"} ${
            indented ? "ml-3 pl-3 border-l border-white/10" : ""
          } ${highlightCls} hover:bg-white/5`}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className={`flex-1 ${isCollapsed ? "hidden" : "inline"}`}>{item.label}</span>
        </button>
      );
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`nav-${item.to.replace("/", "").replace(/\//g, "-")}`}
        title={item.label}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200 ${isCollapsed ? "justify-center" : "justify-start"} ${
            indented ? "ml-3 pl-3 border-l border-white/10" : ""
          } ${
            isActive
              ? item.highlight === "yellow"
                ? "bg-yellow-400/15 text-yellow-200"
                : item.highlight === "green"
                ? "bg-emerald-400/15 text-emerald-200"
                : item.highlight === "pink"
                ? "bg-pink-400/15 text-pink-200"
                : "bg-white/10 text-white"
              : `${highlightCls} hover:bg-white/5`
          }`
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className={`flex-1 ${isCollapsed ? "hidden" : "inline"}`}>{item.label}</span>
        {item.badge === "quotes_total" && badgeFor("hot_leads") > 0 && (
          <span
            data-testid="badge-hot-leads"
            title={`${badgeFor("hot_leads")} ${badgeFor("hot_leads") === 1 ? "richiesta calda" : "richieste calde"} (pubblico/portale, ultime 24h)`}
            className="relative inline-flex h-2.5 w-2.5"
          >
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
        )}
        {item.badge === "quotes_total" && badgeFor("replies") > 0 && (
          <span data-testid="badge-quote-replies" title={`${badgeFor("replies")} risposte da gestire`} className={`bg-rose-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] items-center justify-center animate-pulse ${isCollapsed ? "hidden" : "flex"}`}>
            ✉ {badgeFor("replies") > 99 ? "99+" : badgeFor("replies")}
          </span>
        )}
        {item.to === "/notes" && notesCount > 0 && (
          <span
            data-testid="badge-notes"
            title={`${notesCount} ${notesCount === 1 ? "appunto attivo" : "appunti attivi"}`}
            className={`bg-emerald-600 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] items-center justify-center ${isCollapsed ? "hidden" : "flex"}`}
          >
            {notesCount > 99 ? "99+" : notesCount}
          </span>
        )}
        {badgeCount > 0 && (
          <span
            data-testid={`badge-${item.badge}`}
            className={`text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] items-center justify-center ${isCollapsed ? "hidden" : "flex"} ${
              item.badge === "inbox" && freshInboxAt ? "bg-rose-500 animate-pulse ring-2 ring-rose-300/60" : "bg-blue-600"
            }`}
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        data-testid="crm-sidebar"
        className={`hidden lg:flex ${isCollapsed ? "w-16" : "w-64"} flex-col bg-[#0A192F] text-white sticky top-0 h-screen transition-[width] duration-200`}
      >
        <div className={`${isCollapsed ? "px-3 py-4 justify-center" : "px-6 py-7 justify-start"} border-b border-white/5 flex items-center gap-3 relative`}>
          <div className={`${agency?.logo_url ? "bg-blue-600" : "bg-white"} h-9 w-9 rounded-md flex items-center justify-center overflow-hidden shrink-0 shadow-sm`}>
            {agency?.logo_url ? (
              <img src={agency.logo_url} alt="logo" className="h-full w-full object-cover" />
            ) : (
              <img src="/gestagepro-logo.png" alt="GestAgePro" className="h-full w-full object-contain p-0.5" data-testid="sidebar-brand-logo" />
            )}
          </div>
          <div className={`${isCollapsed ? "hidden" : "block"} min-w-0`}>
            <div className="font-display font-semibold tracking-tight">{agency?.name || "GestAgePro"}</div>
            <div className="text-xs text-slate-400">
              {agency?.name ? "CRM Agenzia" : "Gestionale Agenzie Pro"}
            </div>
            {user?.role && (
              <div
                data-testid="role-badge-sidebar"
                className={`mt-2 inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded border ${ROLE_BADGE[user.role] || "bg-slate-400/15 text-slate-200 border-slate-300/30"}`}
              >
                {ROLE_LABEL[user.role] || user.role}
              </div>
            )}
            {user?.email && (
              <div
                data-testid="sidebar-user-email"
                title={user.email}
                className="mt-1.5 text-[10.5px] text-slate-300/90 truncate max-w-[180px]"
              >
                {user.email}
              </div>
            )}
          </div>
          {/* Toggle manuale collasso sidebar */}
          <button
            type="button"
            data-testid="sidebar-toggle"
            onClick={toggleManualCollapse}
            title={isCollapsed ? "Espandi sidebar" : "Restringi sidebar (mostra solo icone)"}
            aria-label={isCollapsed ? "Espandi sidebar" : "Restringi sidebar"}
            className="absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center shadow-md border border-white/20 z-10 transition-colors"
          >
            {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </button>
        </div>

        <nav className={`flex-1 ${isCollapsed ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto scrollbar-thin`}>
          {visibleTop.map((item) => renderNavLink(item))}
          {visibleGroups.map((g) => {
            const GIcon = g.icon;
            const isOpen = !!openGroups[g.key];
            return (
              <div key={g.key} className="pt-2">
                <button
                  type="button"
                  data-testid={`group-${g.key}`}
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={isOpen}
                  title={g.label}
                  className={`w-full group flex items-center gap-3 rounded-md px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 hover:text-white transition-colors ${isCollapsed ? "justify-center" : "justify-start"}`}
                >
                  <GIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className={`flex-1 text-left ${isCollapsed ? "hidden" : "inline"}`}>{g.label}</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "hidden" : "inline-block"} ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className={`mt-1 space-y-1 ${isCollapsed ? "" : "bg-white/[0.025] rounded-md px-1.5 py-1.5 border-l border-blue-500/20"}`} data-testid={`group-${g.key}-content`}>
                    {g.items.map((it) => {
                      // 🆕 Feb 2026 — Sub-group annidato (es. "Impost. Gestionale")
                      if (it.subgroup) {
                        const SIcon = it.icon;
                        const subKey = `sub-${it.key}`;
                        const subOpen = !!openGroups[subKey];
                        const toneAccent = it.tone === "emerald"
                          ? { bar: "border-emerald-400/60", text: "text-emerald-300 hover:text-emerald-200", chip: "bg-emerald-500/10", icon: "text-emerald-400" }
                          : { bar: "border-blue-500/40", text: "text-slate-300 hover:text-white", chip: "bg-white/5", icon: "text-slate-400" };
                        return (
                          <div key={subKey} className="pt-0.5">
                            <button
                              type="button"
                              data-testid={`subgroup-${it.key}`}
                              onClick={() => toggleGroup(subKey)}
                              aria-expanded={subOpen}
                              title={it.label}
                              className={`w-full group flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${toneAccent.text} ${toneAccent.chip} ${isCollapsed ? "justify-center" : "justify-start"}`}
                            >
                              <SIcon className={`h-3.5 w-3.5 shrink-0 ${toneAccent.icon}`} />
                              <span className={`flex-1 text-left ${isCollapsed ? "hidden" : "inline"}`}>{it.label}</span>
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "hidden" : "inline-block"} ${subOpen ? "rotate-180" : ""}`} />
                            </button>
                            {subOpen && (
                              <div className={`mt-1 space-y-1 ${isCollapsed ? "" : `ml-2 pl-2 border-l ${toneAccent.bar}`}`} data-testid={`subgroup-${it.key}-content`}>
                                {it.items.map((sub) => renderNavLink(sub, true))}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return renderNavLink(it, true);
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={`${isCollapsed ? "px-2" : "px-4"} py-4 border-t border-white/5`}>
          <div className={`${isCollapsed ? "hidden" : "flex"} items-center gap-3 mb-3`}>
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">
                {ROLE_LABEL[user?.role]}
              </div>
            </div>
          </div>
          <div className={`mb-2 flex items-center gap-2 ${isCollapsed ? "flex-col" : "flex-row"}`}>
            <div className={`flex-1 min-w-0 ${isCollapsed ? "w-full" : "w-auto"}`}>
              <NotificationBell />
            </div>
            <button
              data-testid="audio-toggle"
              onClick={toggleAudio}
              title={audioEnabled ? "Suoni notifica attivi (ding=nuovi lead, click=risposta partner). Click per silenziare." : "Suoni notifica silenziati. Click per attivare."}
              className={`p-1.5 rounded-md border transition-colors ${
                audioEnabled
                  ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/25"
                  : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
              }`}
            >
              {audioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
            <button
              data-testid="reset-badges-btn"
              onClick={() => {
                if (totalBadgeCount === 0) return;
                resetAllBadges();
              }}
              title={totalBadgeCount > 0 ? `Azzera ${totalBadgeCount} contator${totalBadgeCount === 1 ? "e" : "i"} delle icone (solo visivo, non marca come letti)` : "Tutti i contatori sono già a zero"}
              disabled={totalBadgeCount === 0}
              className={`p-1.5 rounded-md border transition-colors ${
                totalBadgeCount > 0
                  ? "bg-amber-500/15 border-amber-400/30 text-amber-300 hover:bg-amber-500/25"
                  : "bg-white/5 border-white/10 text-slate-500 opacity-50 cursor-not-allowed"
              }`}
            >
              <BellOff className="h-4 w-4" />
            </button>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            title="Esci"
            className="w-full flex items-center justify-center gap-2 rounded-md bg-white/5 hover:bg-white/10 text-sm py-2 transition-colors"
          >
            <LogOut className="h-4 w-4" /> <span className={isCollapsed ? "hidden" : "inline"}>Esci</span>
          </button>
          {/* 🆕 Feb 2026 — Scarica App Android / Collegamento iOS (nascosto su desktop) */}
          <div className={`mt-2 ${isCollapsed ? "hidden" : "block"}`} data-testid="sidebar-mobile-app">
            <MobileAppDownloadButton variant="sidebar" testIdPrefix="sidebar-mobile-app" />
          </div>
          {/* 🆕 Ripristina anteprime email (rimuove tutte le opzioni "Non chiedermelo più") */}
          <button
            data-testid="reset-email-previews"
            onClick={() => {
              try {
                const skips = JSON.parse(localStorage.getItem("emailPreviewSkip") || "[]");
                const count = Array.isArray(skips) ? skips.length : 0;
                localStorage.removeItem("emailPreviewSkip");
                if (count > 0) {
                  toast.success(`Ripristinate ${count} anteprime email — verranno mostrate al prossimo invio.`);
                } else {
                  toast.info("Nessuna anteprima email da ripristinare.");
                }
              } catch {
                toast.error("Errore ripristino anteprime");
              }
            }}
            className={`mt-2 w-full ${isCollapsed ? "hidden" : "flex"} items-center justify-center gap-2 rounded-md bg-white/5 hover:bg-white/10 text-[11px] py-1.5 transition-colors text-slate-300`}
            title="Ripristina le anteprime email disabilitate con 'Non chiedermelo più'"
          >
            ↻ Ripristina anteprime email
          </button>
          {appVersion?.version && (() => {
            const dateStr = appVersion.commit_date
              ? new Date(appVersion.commit_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '';
            // Mostra solo il numero versione "1.0.192" (senza +sha) per leggibilità
            const verShort = String(appVersion.version).split('+')[0];
            return (
              <div
                data-testid="app-version"
                title={appVersion.commit ? `Commit ${appVersion.commit}` : ''}
                className={`mt-3 text-center text-[10px] uppercase tracking-[0.12em] text-orange-400 font-mono select-text ${isCollapsed ? "hidden" : "block"}`}
              >
                v{verShort}{dateStr ? ` / ${dateStr}` : ''}
              </div>
            );
          })()}
          {/* 🆕 Powered by GestAgePro — mostra il brand alla base della sidebar (solo espansa) */}
          <div
            data-testid="sidebar-powered-by"
            className={`mt-3 pt-3 border-t border-white/5 ${isCollapsed ? "hidden" : "flex"} flex-col items-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity`}
          >
            <img
              src="/gestagepro-logo.png"
              alt="GestAgePro"
              className="h-9 w-auto object-contain bg-white rounded-md px-1.5 py-1 shadow-sm"
              data-testid="sidebar-powered-by-logo"
            />
            <div className="text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
              Powered by GestAgePro
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#0A192F] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {agency?.logo_url ? (
            <img src={agency.logo_url} alt="logo" className="h-6 w-6 rounded object-cover" />
          ) : (
            <img src="/gestagepro-logo.png" alt="GestAgePro" className="h-6 w-6 rounded bg-white object-contain p-0.5" data-testid="mobile-brand-logo" />
          )}
          <span className="font-display font-semibold">{agency?.name || "GestAgePro"}</span>
          <LiveClock compact />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-36"><NotificationBell /></div>
          <button
            data-testid="mobile-logout"
            onClick={handleLogout}
            className="text-xs px-3 py-1.5 rounded bg-white/10"
          >
            Esci
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 lg:p-10 p-4 pt-16 lg:pt-10 max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1920px] mx-auto w-full">
        {/* Live clock + utenti online fluttuanti in alto a destra (desktop) */}
        <div className="hidden lg:flex fixed top-4 right-6 z-30 items-center gap-2" data-testid="topbar-widgets">
          {user?.role === "admin" && <EmailHealthBadge />}
          <OnlineStaffBadge />
          <div className="flex items-center gap-2 rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-sm px-3 py-1.5" data-testid="topbar-live-clock">
            <LiveClock compact />
          </div>
        </div>
        {/* Banner scadenza agenzia (≤20gg) */}
        {agencyInfo?.agency && (agencyInfo.warn || agencyInfo.agency.status !== "active") && (
          <div
            data-testid="agency-expiry-banner"
            className={`mb-4 rounded-lg border p-3 flex items-center gap-3 ${
              agencyInfo.agency.status === "suspended" ? "border-rose-300 bg-rose-50 text-rose-900" :
              (agencyInfo.days_left ?? 0) < 0 ? "border-rose-300 bg-rose-50 text-rose-900" :
              "border-amber-300 bg-amber-50 text-amber-900"
            }`}
          >
            <span className="text-xl">⚠</span>
            <div className="flex-1 min-w-0 text-sm">
              {agencyInfo.agency.status === "suspended" ? (
                <><strong>Account sospeso.</strong> Contatta l&apos;amministratore per la riattivazione.</>
              ) : (agencyInfo.days_left ?? 0) < 0 ? (
                <><strong>Abbonamento scaduto da {Math.abs(agencyInfo.days_left)} giorni.</strong> Rinnova per continuare a usare il servizio.</>
              ) : (
                <><strong>Abbonamento in scadenza tra {agencyInfo.days_left} giorni.</strong> Effettua il pagamento per evitare interruzioni.</>
              )}
              {agencyInfo.payment_pending && <span className="block text-[11px] mt-1">💰 Pagamento in attesa di conferma dal super-admin.</span>}
            </div>
            {user?.role === "admin" && !agencyInfo.payment_pending && (
              <button
                data-testid="payment-notify-btn"
                onClick={notifyPayment}
                disabled={payingPending}
                className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 font-medium disabled:opacity-50"
              >
                💳 Ho pagato il rinnovo
              </button>
            )}
          </div>
        )}
        {(location.pathname.startsWith("/admin") || location.pathname === "/users") && (
          <div className="mb-4 flex justify-end print:hidden">
            <button
              data-testid="close-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors shadow-sm"
              title="Chiudi e torna alla Dashboard"
            >
              <X className="h-4 w-4" />
              Chiudi e vai alla Dashboard
            </button>
          </div>
        )}
        <Outlet />
      </main>

      {/* Floating Quick Note button — appunto al volo in tutte le aree */}
      <QuickNoteFab />
      <PartnerOfferPopup />
      <TextZoomControl />
      <DownloadProgressOverlay />

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 grid grid-cols-5 z-20">
        {visibleTop.slice(0, 5).map((item) => {
          const Icon = item.icon;
          const badgeCount = item.badge ? badgeFor(item.badge) : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center py-2 text-[10px] ${
                  isActive ? "text-blue-600" : "text-slate-500"
                }`
              }
            >
              <Icon className="h-4 w-4 mb-0.5" />
              <span className="uppercase tracking-wide">{item.label.slice(0, 8)}</span>
              {badgeCount > 0 && (
                <span className="absolute top-1 right-1/4 bg-blue-600 text-white text-[9px] font-semibold rounded-full px-1 min-w-[16px] h-[16px] flex items-center justify-center">
                  {badgeCount > 99 ? "99+" : badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Dialog cambio password staff */}
      <Dialog open={pwdOpen} onOpenChange={(o) => { setPwdOpen(o); if (!o) setPwdForm({ current: "", next: "", confirm: "" }); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-blue-600" /> Cambia password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password attuale <span className="text-red-500">*</span></Label>
              <Input
                data-testid="staff-pwd-current"
                type="password"
                autoComplete="current-password"
                value={pwdForm.current}
                onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                placeholder="La tua password attuale"
              />
            </div>
            <div>
              <Label>Nuova password <span className="text-red-500">*</span></Label>
              <Input
                data-testid="staff-pwd-next"
                type="password"
                autoComplete="new-password"
                value={pwdForm.next}
                onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
                placeholder="Almeno 8 caratteri"
              />
              <p className="text-[11px] text-slate-500 mt-1">Almeno 8 caratteri. Usa lettere, numeri e simboli per maggiore sicurezza.</p>
            </div>
            <div>
              <Label>Conferma nuova password <span className="text-red-500">*</span></Label>
              <Input
                data-testid="staff-pwd-confirm"
                type="password"
                autoComplete="new-password"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                placeholder="Ripeti la nuova password"
                className={pwdForm.confirm && pwdForm.confirm !== pwdForm.next ? "border-rose-500" : ""}
              />
              {pwdForm.confirm && pwdForm.confirm !== pwdForm.next && (
                <p className="text-[11px] text-rose-600 mt-1">Le password non coincidono</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdBusy}>Annulla</Button>
            <Button
              data-testid="staff-pwd-submit"
              onClick={submitChangePassword}
              disabled={pwdBusy || !pwdForm.current || !pwdForm.next || pwdForm.next !== pwdForm.confirm}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            >
              {pwdBusy ? "Salvataggio…" : "Aggiorna password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Popup versione demo (mostrato ad ogni apertura app se agency.is_demo) */}
      <Dialog open={demoPopupOpen} onOpenChange={setDemoPopupOpen}>
        <DialogContent className="max-w-md" data-testid="demo-agency-popup">
          <DialogHeader>
            <DialogTitle>Versione Demo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Stai usando la <strong>versione demo</strong> con limiti d&apos;uso.</p>
            <p>Vuoi richiedere l&apos;attivazione definitiva?</p>
            {agencyInfo?.agency?.expires_at && (
              <p className="text-xs text-slate-500 mt-2">
                Scadenza demo: <strong>{new Date(agencyInfo.agency.expires_at).toLocaleDateString("it-IT")}</strong>
                {typeof agencyInfo.days_left === "number" && (
                  <> · {agencyInfo.days_left >= 0 ? `${agencyInfo.days_left} giorni rimanenti` : `scaduta da ${Math.abs(agencyInfo.days_left)} giorni`}</>
                )}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              data-testid="demo-popup-no"
              variant="outline"
              onClick={() => setDemoPopupOpen(false)}
            >
              No, resta in demo
            </Button>
            <Button
              data-testid="demo-popup-yes"
              className="bg-[#0A192F] hover:bg-[#1E293B] text-white"
              onClick={() => {
                setDemoPopupOpen(false);
                window.location.href = "/landing#plans";
              }}
            >
              Sì, vai ai piani
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
